import type { DrawGuessEvent, DrawGuessState, DrawGuessStroke } from "@topzle/game-draw-guess";
import type { ActiveRoomSnapshot } from "@topzle/types";
import {
  Bot,
  Brush,
  Clock3,
  Eraser,
  LoaderCircle,
  RotateCcw,
  Send,
  SkipForward,
  Sparkles,
  Trash2,
  Trophy,
  Undo2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  classifyDrawGuessStrokes,
  type DrawGuessPrediction,
  resampleDrawGuessPoints,
  warmDrawGuessClassifier,
} from "../drawGuessClassifier";

export type DrawGuessPublicState = Omit<DrawGuessState, "word"> & { word: string | null };

const drawingColors = ["#17243a", "#e83f53", "#2872ca", "#14935d", "#e3a50d"] as const;

function participantName(
  playerId: string,
  participantId: string,
  participants: ActiveRoomSnapshot["participants"],
) {
  if (playerId === participantId) return "You";
  if (playerId.startsWith("ai:")) return "Topzle AI";
  return participants.find((participant) => participant.id === playerId)?.displayName ?? "Player";
}

function strokePoints(stroke: DrawGuessStroke): string {
  return stroke.points.map(([x, y]) => `${x},${y}`).join(" ");
}

export function DrawGuessBoard({
  state,
  participantId,
  participants,
  aiDifficulty,
  serverError,
  playAgain,
  onAction,
}: {
  state: DrawGuessPublicState;
  participantId: string;
  participants: ActiveRoomSnapshot["participants"];
  aiDifficulty: "easy" | "medium" | "hard";
  serverError: string | null;
  playAgain: React.ReactNode;
  onAction: (action: DrawGuessEvent) => boolean;
}) {
  const [color, setColor] = useState<(typeof drawingColors)[number]>("#17243a");
  const [tool, setTool] = useState<"draw" | "erase">("draw");
  const [toolSize, setToolSize] = useState(12);
  const [draftStroke, setDraftStroke] = useState<DrawGuessStroke | null>(null);
  const [guess, setGuess] = useState("");
  const [now, setNow] = useState(Date.now());
  const [aiThinking, setAiThinking] = useState(false);
  const [aiPredictions, setAiPredictions] = useState<DrawGuessPrediction[]>([]);
  const [aiError, setAiError] = useState<string | null>(null);
  const attemptedAiGuesses = useRef(new Set<string>());
  const activePointerId = useRef<number | null>(null);
  const draftStrokeRef = useRef<DrawGuessStroke | null>(null);
  const onActionRef = useRef(onAction);
  const strokesRef = useRef(state.strokes);
  strokesRef.current = state.strokes;
  const isDrawer = state.drawerId === participantId;
  const canDraw = isDrawer && state.phase === "drawing";
  const canGuess =
    state.players.includes(participantId) &&
    !isDrawer &&
    state.phase === "drawing" &&
    !participantId.startsWith("ai:");
  const secondsLeft = Math.max(0, Math.ceil((state.deadline - now) / 1000));
  const latestGuesses = state.guesses.slice(-6).reverse();
  const sortedScores = useMemo(
    () => Object.entries(state.scores).sort((left, right) => right[1] - left[1]),
    [state.scores],
  );
  const drawingRevision = useMemo(
    () =>
      state.strokes
        .map((stroke) => {
          const lastPoint = stroke.points.at(-1);
          return `${stroke.id}:${stroke.mode}:${stroke.points.length}:${lastPoint?.join(",") ?? ""}`;
        })
        .join("|"),
    [state.strokes],
  );

  useEffect(() => {
    onActionRef.current = onAction;
  }, [onAction]);

  useEffect(() => {
    if (state.phase !== "drawing") return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(interval);
  }, [state.phase]);

  useEffect(() => {
    if (state.round < 1) return;
    attemptedAiGuesses.current = new Set();
    setAiPredictions([]);
    setAiError(null);
  }, [state.round]);

  useEffect(() => {
    if (!state.aiEnabled || !isDrawer) return;
    void warmDrawGuessClassifier().catch((error) =>
      setAiError(error instanceof Error ? error.message : "The local AI model could not load"),
    );
  }, [isDrawer, state.aiEnabled]);

  useEffect(() => {
    if (!state.aiEnabled || !isDrawer || state.phase !== "drawing" || !drawingRevision) return;
    const strokes = strokesRef.current;
    const delay = aiDifficulty === "easy" ? 1_400 : aiDifficulty === "hard" ? 550 : 900;
    let cancelled = false;
    let followupTimer: number | null = null;
    let resolveFollowup: (() => void) | null = null;
    const timer = window.setTimeout(async () => {
      if (cancelled) return;
      setAiThinking(true);
      try {
        const predictions = await classifyDrawGuessStrokes(strokes);
        if (cancelled) return;
        setAiPredictions(predictions);
        const guessLimit = aiDifficulty === "hard" ? 3 : aiDifficulty === "medium" ? 2 : 1;
        const nextGuesses = predictions
          .filter((prediction) => !attemptedAiGuesses.current.has(prediction.label))
          .slice(0, guessLimit);
        for (const nextGuess of nextGuesses) {
          if (cancelled) return;
          attemptedAiGuesses.current.add(nextGuess.label);
          if (
            !onActionRef.current({
              type: "ai_guess",
              guess: nextGuess.label,
              confidence: nextGuess.confidence,
            })
          ) {
            attemptedAiGuesses.current.delete(nextGuess.label);
            return;
          }
          if (nextGuess !== nextGuesses.at(-1))
            await new Promise<void>((resolve) => {
              resolveFollowup = resolve;
              followupTimer = window.setTimeout(
                () => {
                  resolveFollowup = null;
                  resolve();
                },
                aiDifficulty === "hard" ? 450 : 700,
              );
            });
        }
      } catch (error) {
        if (cancelled) return;
        setAiError(error instanceof Error ? error.message : "Local drawing recognition failed");
      } finally {
        if (!cancelled) setAiThinking(false);
      }
    }, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (followupTimer !== null) window.clearTimeout(followupTimer);
      resolveFollowup?.();
      setAiThinking(false);
    };
  }, [aiDifficulty, drawingRevision, isDrawer, state.aiEnabled, state.phase]);

  const pointFromPointer = (event: React.PointerEvent<SVGSVGElement>): [number, number] => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return [
      Math.round(
        Math.max(0, Math.min(1000, ((event.clientX - bounds.left) / bounds.width) * 1000)),
      ),
      Math.round(
        Math.max(0, Math.min(1000, ((event.clientY - bounds.top) / bounds.height) * 1000)),
      ),
    ];
  };

  const finishStroke = (event: React.PointerEvent<SVGSVGElement>) => {
    const stroke = draftStrokeRef.current;
    if (activePointerId.current !== event.pointerId || !stroke) return;
    activePointerId.current = null;
    draftStrokeRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (stroke.points.length > 1)
      onActionRef.current({
        type: "stroke",
        stroke: { ...stroke, points: resampleDrawGuessPoints(stroke.points) },
      });
    setDraftStroke(null);
  };

  const submitGuess = () => {
    const value = guess.trim();
    if (!value || !canGuess) return;
    if (onAction({ type: "guess", guess: value })) setGuess("");
  };

  return (
    <div
      className={canGuess ? "active-game draw-guess-game can-guess" : "active-game draw-guess-game"}
    >
      <div className="draw-guess-topbar">
        <div>
          <span>
            ROUND {state.round}/{state.totalRounds}
          </span>
          <strong>
            {state.phase === "drawing"
              ? isDrawer
                ? `Draw: ${state.word ?? "loading prompt…"}`
                : `${participantName(state.drawerId, participantId, participants)} is drawing`
              : state.phase === "round_result"
                ? `It was ${state.word ?? "…"}`
                : "Match complete"}
          </strong>
        </div>
        <div className={secondsLeft <= 10 ? "draw-timer urgent" : "draw-timer"}>
          <Clock3 />
          <b>{state.phase === "drawing" ? secondsLeft : "—"}</b>
        </div>
      </div>

      <div className="draw-guess-layout">
        <section className="draw-canvas-shell">
          <svg
            className={
              canDraw ? `draw-canvas drawable${tool === "erase" ? " erasing" : ""}` : "draw-canvas"
            }
            viewBox="0 0 1000 1000"
            role="img"
            aria-label={canDraw ? "Drawing canvas" : "Current drawing"}
            onPointerDown={(event) => {
              if (!canDraw || activePointerId.current !== null) return;
              activePointerId.current = event.pointerId;
              event.currentTarget.setPointerCapture(event.pointerId);
              const stroke: DrawGuessStroke = {
                id: crypto.randomUUID(),
                mode: tool,
                color: tool === "erase" ? "#ffffff" : color,
                size: tool === "erase" ? Math.max(24, toolSize) : toolSize,
                points: [pointFromPointer(event)],
              };
              draftStrokeRef.current = stroke;
              setDraftStroke(stroke);
            }}
            onPointerMove={(event) => {
              if (activePointerId.current !== event.pointerId) return;
              const point = pointFromPointer(event);
              const stroke = draftStrokeRef.current;
              if (!stroke) return;
              const previous = stroke.points.at(-1);
              if (previous && Math.hypot(point[0] - previous[0], point[1] - previous[1]) < 6)
                return;
              const retainedPoints =
                stroke.points.length >= 2_048
                  ? resampleDrawGuessPoints(stroke.points, 1_024)
                  : stroke.points;
              const nextStroke = { ...stroke, points: [...retainedPoints, point] };
              draftStrokeRef.current = nextStroke;
              setDraftStroke(nextStroke);
            }}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
            onLostPointerCapture={finishStroke}
          >
            <title>{isDrawer ? "Draw the secret prompt" : "Guess the drawing"}</title>
            <rect width="1000" height="1000" rx="34" fill="#fff" />
            {state.strokes.map((stroke) => (
              <polyline
                points={strokePoints(stroke)}
                fill="none"
                stroke={stroke.mode === "erase" ? "#ffffff" : stroke.color}
                strokeWidth={stroke.size}
                strokeLinecap="round"
                strokeLinejoin="round"
                key={stroke.id}
              />
            ))}
            {draftStroke && (
              <polyline
                points={strokePoints(draftStroke)}
                fill="none"
                stroke={draftStroke.mode === "erase" ? "#ffffff" : draftStroke.color}
                strokeWidth={draftStroke.size}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
            {state.strokes.length === 0 && !draftStroke && (
              <text x="500" y="500" textAnchor="middle" className="draw-empty-text">
                {canDraw ? "Start drawing here" : "Waiting for the first stroke…"}
              </text>
            )}
          </svg>

          {canDraw && (
            <div className="draw-tools" role="toolbar" aria-label="Drawing tools">
              <div className="draw-tool-modes">
                <button
                  type="button"
                  className={tool === "draw" ? "selected" : ""}
                  onClick={() => setTool("draw")}
                  aria-label="Use brush"
                  aria-pressed={tool === "draw"}
                >
                  <Brush />
                  <span>Brush</span>
                </button>
                <button
                  type="button"
                  className={tool === "erase" ? "selected" : ""}
                  onClick={() => setTool("erase")}
                  aria-label="Use eraser"
                  aria-pressed={tool === "erase"}
                >
                  <Eraser />
                  <span>Eraser</span>
                </button>
              </div>
              <div className="draw-colors">
                {drawingColors.map((entry) => (
                  <button
                    type="button"
                    className={entry === color && tool === "draw" ? "selected" : ""}
                    style={{ background: entry }}
                    onClick={() => {
                      setColor(entry);
                      setTool("draw");
                    }}
                    aria-label={`Use ${entry} brush`}
                    aria-pressed={entry === color && tool === "draw"}
                    key={entry}
                  />
                ))}
              </div>
              <input
                type="range"
                min="6"
                max="48"
                value={toolSize}
                onChange={(event) => setToolSize(Number(event.target.value))}
                aria-label={tool === "erase" ? "Eraser size" : "Brush size"}
              />
              <button type="button" onClick={() => onAction({ type: "undo" })}>
                <Undo2 /> Undo
              </button>
              <button type="button" onClick={() => onAction({ type: "clear" })}>
                <Trash2 /> Clear
              </button>
              <button
                type="button"
                className="draw-skip"
                onClick={() => onAction({ type: "skip" })}
              >
                <SkipForward /> Skip
              </button>
            </div>
          )}

          {state.phase === "round_result" && (
            <div className="draw-round-result">
              {state.roundReason === "correct_guess" ? <Sparkles /> : <Clock3 />}
              <strong>
                {state.roundReason === "correct_guess"
                  ? `${participantName(state.roundWinnerId ?? "", participantId, participants)} guessed it!`
                  : state.roundReason === "skipped"
                    ? "Prompt skipped"
                    : "Time ran out"}
              </strong>
              <span>The word was “{state.word}”.</span>
              {isDrawer && (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => onAction({ type: "next_round" })}
                >
                  {state.round >= state.totalRounds ? <Trophy /> : <RotateCcw />}
                  {state.round >= state.totalRounds ? "See final scores" : "Next round"}
                </button>
              )}
            </div>
          )}

          {state.phase === "finished" && (
            <div className="draw-round-result final">
              <Trophy />
              <strong>Final scores</strong>
              {playAgain}
            </div>
          )}
        </section>

        <aside className={canGuess ? "draw-guess-panel can-guess" : "draw-guess-panel"}>
          {state.aiEnabled && state.phase !== "finished" ? (
            <div className="local-ai-card">
              <span className="local-ai-icon">
                <Bot />
                {aiThinking && <LoaderCircle className="local-ai-spinner" />}
              </span>
              <div>
                <strong>{aiThinking ? "Topzle AI is looking…" : "On-device AI"}</strong>
                <small>Local neural model · no external AI API</small>
              </div>
              {aiPredictions.length > 0 && (
                <div className="ai-predictions">
                  {aiPredictions.slice(0, 3).map((prediction) => (
                    <span key={prediction.label}>
                      {prediction.label}
                      <small>{Math.round(prediction.confidence * 100)}%</small>
                    </span>
                  ))}
                </div>
              )}
              {aiError && <small className="draw-error">{aiError}</small>}
            </div>
          ) : (
            <div className="draw-scoreboard">
              <span>SCORES</span>
              {sortedScores.slice(0, 6).map(([playerId, score], index) => (
                <div
                  className={
                    state.phase === "finished" && score === sortedScores[0]?.[1]
                      ? "final-winner"
                      : ""
                  }
                  key={playerId}
                >
                  <b>{index + 1}</b>
                  <span>{participantName(playerId, participantId, participants)}</span>
                  <strong>{score}</strong>
                </div>
              ))}
            </div>
          )}

          <div className="guess-feed" aria-live="polite">
            <span>GUESSES</span>
            {latestGuesses.length === 0 ? (
              <small>No guesses yet.</small>
            ) : (
              latestGuesses.map((entry) => (
                <div
                  className={entry.correct ? "correct" : ""}
                  key={`${entry.playerId}:${entry.at}`}
                >
                  <b>{participantName(entry.playerId, participantId, participants)}</b>
                  <span>{entry.text}</span>
                  {entry.confidence !== null && (
                    <small>{Math.round(entry.confidence * 100)}%</small>
                  )}
                </div>
              ))
            )}
          </div>

          {canGuess && (
            <form
              className="draw-guess-form"
              onSubmit={(event) => {
                event.preventDefault();
                submitGuess();
              }}
            >
              <input
                value={guess}
                onChange={(event) => setGuess(event.target.value)}
                placeholder="Type your guess…"
                maxLength={48}
                autoComplete="off"
              />
              <button type="submit" aria-label="Submit guess" disabled={!guess.trim()}>
                <Send />
                <span>Guess</span>
              </button>
            </form>
          )}

          {(serverError || aiError) && (
            <div className="draw-error" role="status">
              {serverError ?? aiError}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
